import { SlidePlayer, type V2Pack } from 'ai-tutor-player';
import { Loader2, X } from 'lucide-react';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { AppShell } from '@/components/shell/AppShell';
import { AudioReportPanel } from '@/components/lesson-packs/AudioReportPanel';
import { TeachStudio } from '@/components/teach/TeachStudio';
import { MainWorkspace } from '@/components/workspace/MainWorkspace';
import { api } from '@/lib/api';
import { useEditorHotkeys } from '@/lib/keymap';
import type { LessonPack, Script } from '@/lib/schema.types';
import { useEditorStore } from '@/store/editor.store';


export default function EditLessonPackPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const previewing = searchParams.get('preview') === '1';
  // Preview the LIVE edited slides from the store, not the (possibly stale)
  // fetched copy, so unsaved timeline edits show up.
  const liveSlides = useEditorStore((s) => s.script?.slides);
  const { data, error, isLoading } = useSWR(
    id ? ['/lesson-packs', id, 'full'] : null,
    () => api.lessonPacks.get(id),
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    if (data) {
      useEditorStore.getState().hydrate(packToScript(data), 'pack', {
        entryBeatId: data.entryBeatId ?? null,
        publishStateAtLoad: data.publishState,
      });
    }
  }, [data]);


  useEffect(() => {
    return () => {
      void useEditorStore.getState().drainAndReset();
    };
  }, []);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const status = useEditorStore.getState().syncStatus;
      if (status === 'pending' || status === 'saving' || status === 'error') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEditorHotkeys();

  if (isLoading)
    return (
      <FullScreenStatus
        icon={<Loader2 className="animate-spin" />}
        text="Loading lesson pack…"
      />
    );
  if (error)
    return (
      <FullScreenStatus
        text={`Couldn't load pack. ${error instanceof Error ? error.message : ''}`}
      />
    );
  if (!data) return null;

  if (data.status !== 'done') {
    return (
      <FullScreenStatus
        icon={<Loader2 className="animate-spin" />}
        text={`Pack is still ${data.status} — can't open the editor yet.`}
      />
    );
  }

  return (
    <>
      <AppShell>
        <MainWorkspace />
      </AppShell>
      <TeachStudioMount />
      <AudioReportPanel packId={id} />
      {previewing && data && (
        <SharedPreview
          pack={
            {
              ...(data as unknown as V2Pack),
              slides: (liveSlides ?? (data as unknown as V2Pack).slides) as V2Pack['slides'],
            } as V2Pack
          }
          onClose={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('preview');
            setSearchParams(next, { replace: true });
          }}
        />
      )}
    </>
  );
}

/**
 * Full-screen Preview rendered by the SHARED player (`ai-tutor-player`) — the
 * same component the Electron classroom will use, on a white background. The
 * lesson pack fetched here is already a V2Pack-shaped doc, so it's fed straight
 * in; no clicker/TTS ports are provided (web preview = clip audio only).
 */
function SharedPreview({ pack, onClose }: { pack: V2Pack; onClose: () => void }) {
  // Chalk fonts are registered app-wide in main.tsx (a per-mount cleanup here
  // would null the global port and break the Teach page's play mode).

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black">
      <button
        onClick={onClose}
        className="fixed right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white/80 backdrop-blur transition hover:bg-white/20 hover:text-white"
        title="Exit preview (Esc)"
      >
        <X size={16} />
      </button>
      <div className="flex flex-1 items-center justify-center overflow-y-auto">
        <div className="mx-auto w-full max-w-[1100px] px-6">
          <SlidePlayer pack={pack} config={{ background: 'white' }} />
        </div>
      </div>
    </div>
  );
}


function packToScript(pack: LessonPack): Script {
  return {
    id: pack._id,
    version: String(pack.schemaVersion ?? 1),
    label: pack.title,
    metadata: {
      subject: pack.subject,
      chapter: pack.chapterReference,
      topic: pack.inputMetadata?.topic,
      classLevel: pack.grade,
    },
    slides: pack.slides,
    createdAt: pack.createdAt,
    updatedAt: pack.updatedAt,
  };
}

function TeachStudioMount() {
  const teachingSlideId = useEditorStore((s) => s.teachingSlideId);
  if (!teachingSlideId) return null;
  return (
    <TeachStudio
      slideId={teachingSlideId}
      onClose={() => useEditorStore.getState().setTeachingSlideId(null)}
    />
  );
}

function FullScreenStatus({ text, icon }: { text: string; icon?: React.ReactNode }) {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-bg text-sm text-fg-muted">
      <div className="flex items-center gap-2">
        {icon}
        <span>{text}</span>
      </div>
    </div>
  );
}
